import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { isValid, format } from 'date-fns';

// Create singleton formatters for better performance
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const compactCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

const numberFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 0,
});

const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

export const formatCurrency = (amount: number): string => {
  if (!isFinite(amount)) return 'N/A';
  return currencyFormatter.format(amount);
};

export const formatCompactCurrency = (amount: number): string => {
  if (!isFinite(amount)) return 'N/A';
  return compactCurrencyFormatter.format(amount);
};

export const formatNumber = (value: number): string => {
  if (!isFinite(value)) return 'N/A';
  return numberFormatter.format(value);
};

export const formatPercent = (value: number): string => {
  if (!isFinite(value)) return 'N/A';
  return percentFormatter.format(value / 100);
};

export const formatDate = (date: Date | number | string): string => {
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (!isValid(dateObj)) return 'Invalid date';
    return format(dateObj, 'MMMM d, yyyy');
  } catch (error) {
    return 'Invalid date';
  }
};

export const parseInputValue = (value: string): number => {
  const cleaned = value.replace(/[^0-9.-]+/g, '');
  const parsed = Number(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

// Format large numbers with K/M/B abbreviations
export const formatCompactNumber = (value: number): string => {
  if (!isFinite(value)) return 'N/A';
  if (value >= 1000000000) {
    return `$${Math.round(value / 1000000000)}B`;
  }
  if (value >= 1000000) {
    return `$${Math.round(value / 1000000)}M`;
  }
  if (value >= 1000) {
    return `$${Math.round(value / 1000)}K`;
  }
  return `$${Math.round(value)}`;
};

// Safe date parsing
export const parseSafeDate = (date: Date | number | string | null): Date | null => {
  if (!date) return null;
  
  const parsed = new Date(date);
  return isValid(parsed) ? parsed : null;
};

// Optimized debounce utility
export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>): void => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Optimized memoization helper with WeakMap for better garbage collection
export function memoize<T extends (...args: any[]) => any>(
  func: T,
  resolver?: (...args: Parameters<T>) => string
): T {
  const cache = new Map<string, ReturnType<T>>();
  const weakCache = new WeakMap();

  return ((...args: Parameters<T>): ReturnType<T> => {
    const key = resolver ? resolver(...args) : JSON.stringify(args);
    
    // Try WeakMap first for object keys
    if (typeof key === 'object' && key !== null) {
      if (weakCache.has(key)) {
        return weakCache.get(key);
      }
      const result = func(...args);
      weakCache.set(key, result);
      return result;
    }
    
    // Fall back to regular Map for primitive keys
    if (cache.has(key)) {
      return cache.get(key)!;
    }
    const result = func(...args);
    cache.set(key, result);
    return result;
  }) as T;
}
// Add this new function at the end of your existing utils.ts file

/**
 * Returns true if the current viewport is considered mobile
 */
export const isMobileViewport = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
};